/**
 * M-A12 — Add Skill editor modal.
 *
 * Sprint 133 Stream C. Grants ``Skill#editors`` to a user via Keto by
 * calling ``POST /admin/skills/{skill_id}/editors``. Group-restrict
 * (``Tenant#admins`` subject_set) is deferred to M-A13.
 */

import { useEffect, useState } from 'react';
import { DiscardChangesDialog } from '@vanguard-atlas/shared';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTenantMembers } from '@/features/permissions/hooks';
import { useAddSkillEditor } from '@/features/skills';
import { useI18n } from '@/i18n';
import { getErrorMessage } from '@/lib/api-client';
import { userDisplayLabel } from '@/lib/user-label';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  skillId: string;
  existingUserIds: string[];
}

export function SkillEditorAddModal({
  open,
  onOpenChange,
  skillId,
  existingUserIds,
}: Props) {
  const { data: tenantMembers, isLoading: loadingMembers } = useTenantMembers();
  const [query, setQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const isDirty = selectedUserId !== '';
  const discardLabels = {
    title: 'Discard changes?',
    message: 'You have unsaved changes. If you close now, they will be lost.',
    confirm: 'Discard',
    cancel: 'Keep editing',
  };
  const handleClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onOpenChange(false);
  };
  const add = useAddSkillEditor(skillId);

  const { t } = useI18n();

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedUserId('');
    }
  }, [open]);

  const normalizedQuery = query.trim().toLowerCase();
  const matches = (tenantMembers?.items || [])
    .filter((member) => !existingUserIds.includes(member.user_id))
    .filter((member) => {
      if (!normalizedQuery) return true;
      return [member.name, member.email, member.user_id]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(normalizedQuery));
    })
    .slice(0, 8);

  const alreadyExists = selectedUserId && existingUserIds.includes(selectedUserId);
  const isValid = selectedUserId.length > 0 && !alreadyExists;

  async function handleSubmit() {
    if (!isValid) return;
    try {
      await add.mutateAsync(selectedUserId);
      toast.success(
        t('admin.skills.editorAdd.granted', { userId: selectedUserId }),
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) { handleClose(); } }}>
      <DialogContent
        dirty={isDirty}
        discardTitle={discardLabels.title}
        discardMessage={discardLabels.message}
        discardConfirmLabel={discardLabels.confirm}
        discardCancelLabel={discardLabels.cancel}
      >
        <DialogHeader>
          <DialogTitle>{t('admin.skills.editorAdd.title')}</DialogTitle>
          <DialogDescription>
            {t('admin.skills.editorAdd.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="editor-user-search">{t('admin.skills.editorAdd.tenantMember')}</Label>
          <Input
            id="editor-user-search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedUserId('');
            }}
            placeholder={t('admin.skills.editorAdd.searchPlaceholder')}
            autoFocus
          />
          {loadingMembers ? (
            <p className="text-xs text-muted-foreground">{t('admin.skills.editorAdd.loadingMembers')}</p>
          ) : matches.length > 0 ? (
            <div className="max-h-56 space-y-1 overflow-auto rounded-md border p-1">
              {matches.map((member) => {
                const isSelected = member.user_id === selectedUserId;
                return (
                  <button
                    key={member.user_id}
                    type="button"
                    onClick={() => {
                      setSelectedUserId(member.user_id);
                      setQuery(member.email || member.name || '');
                    }}
                    className={`w-full rounded-md px-3 py-2 text-left text-sm transition-colors ${
                      isSelected
                        ? 'bg-accent text-accent-foreground'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <div className="font-medium">
                      {userDisplayLabel(member.email, member.name, member.user_id, t('admin.skills.editorAdd.emailUnavailable'))}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {member.email ? member.name || member.email : userDisplayLabel(null, null, member.user_id, t('admin.skills.editorAdd.emailUnavailable'))}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : query.trim() ? (
            <p className="text-xs text-muted-foreground">
              {t('admin.skills.editorAdd.noMatches')}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {t('admin.skills.editorAdd.startTyping')}
            </p>
          )}
          {alreadyExists && (
            <p className="text-xs text-destructive">
              {t('admin.skills.editorAdd.alreadyEditor')}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={add.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || add.isPending}>
            {add.isPending ? t('admin.skills.editorAdd.adding') : t('admin.skills.editorAdd.addEditor')}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* ADR-114 enmienda 1: confirmación de descarte */}
      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          onOpenChange(false);
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
        title={discardLabels.title}
        message={discardLabels.message}
        confirmLabel={discardLabels.confirm}
        cancelLabel={discardLabels.cancel}
      />
    </Dialog>
  );
}

export default SkillEditorAddModal;
